import {
  Avatar,
  DataTable,
  InfoIcon,
  Label,
  PDFModal,
  ReportToolbar,
  Tooltip,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userHasPermission } from '../../api/firebase/account';
import { fetchBillingDetails } from '../../api/firebase/helpers/users';
import { fetchOrganizationUsers } from '../../api/firebase/users';
import { useContacts } from '../../contacts/contactsHooks';
import { phoneNumberParse } from '../../helpers/utils';
import useRooms from '../../hooks/useRooms';
import { logError } from '../../rollbar';
import { NoResults } from '../Shared/NoResults';

// Using relative paths for SVG files
import { ReactComponent as AppIcon } from '../../assets/images/svg/app.svg';
import { ReactComponent as CheckmarkIcon } from '../../assets/images/svg/checkmark.svg';

import { getLocations } from '../../redux/actions/locationActions';
import { InviteStatus } from './InviteStatus';
import { getFamilyInviteStatusLabel, getFamilyRelationshipLabel } from './familyDirectoryExportUtils';
import { generateParentsListingPDF } from './generateParentsListPDF';
import { generateParentsListingCSV } from './generateParentsListingCSV';

export function ParentsListingTable({ studentsList: allStudentsList, stateFilter }) {
  const { t } = useTranslation();
  const canListBilling = userHasPermission('can_list_billing');
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [studentBillingDetails, setStudentBillingDetails] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const { contacts } = useContacts();
  const locations = getLocations();
  const locationName = locations[0]?.name;
  const rooms = useRooms();
  const organizationId = allStudentsList[0]?.organization || allStudentsList[0]?.defaultOrganization;

  const NUM_CONTACTS_PER_PAGE = 50;

  async function getOrgUser() {
    const allUsers = await fetchOrganizationUsers(organizationId);
    if (allUsers) {
      setAllUsers(allUsers.list);
    }
  }

  useEffect(() => {
    getOrgUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudentsList, contacts]);

  const studentsList = useMemo(() => {
    const autopayFilter = stateFilter.filters.autopay;
    if (!autopayFilter) return allStudentsList;

    return allStudentsList.filter(
      (student: any) => studentBillingDetails.includes(student.id) === (autopayFilter === 'yes')
    );
  }, [allStudentsList, stateFilter.filters.autopay, studentBillingDetails]);

  useEffect(() => {
    const fetchData = async () => {
      const studentsListIds = studentsList.map((obj: { id: any }) => obj.id);
      try {
        studentsListIds.forEach(async (id: any) => {
          const billing = await fetchBillingDetails(organizationId, id);
          if (billing.data.hasPaymentMethod === true && billing.data.canChargeAutomatically === true) {
            setStudentBillingDetails((prev) => [...prev, billing.studentId]);
          }
        });
      } catch (e) {
        logError('Error trying to get billing details for responsible parent.', e);
      }
    };
    if (canListBilling) {
      fetchData();
    }
  }, [canListBilling, organizationId, studentsList]);

  // Shared label components to avoid duplication
  const createLabelWithTooltip = (text: string, tooltipContent: string, className?: string) => (
    <Label extraClasses="flex flex-row align-middle mb-0 text-base md:text-sm text-gray-700 md:text-gray-800">
      {t(text)}
      <Tooltip content={t(tooltipContent)} className={className} action="hover">
        <InfoIcon className="ml-1 h-5" />
      </Tooltip>
    </Label>
  );

  // Mobile-specific columns that work with student data structure
  const mobileColumns = [
    {
      label: t('familyDirectory.columns.students'),
      fieldName: 'studentName',
      className: 'py-2',
      renderCell: (student: any) => {
        return (
          <div className="flex items-center">
            <div className="hidden h-11 w-11 flex-shrink-0 md:block">
              <div className="flex h-11 w-11 justify-center rounded-full text-center">
                {student.studentPicture ? (
                  <Avatar
                    name={student.studentPicture}
                    size={WidgetSizeEnum.MEDIUM}
                    photoURL={student.studentPicture}
                  />
                ) : (
                  <Avatar name={student.studentName} size={WidgetSizeEnum.MEDIUM} />
                )}
              </div>
            </div>
            <div className="ml-4 cursor-default">
              <Label extraClasses="font-medium">{student.studentName}</Label>
              <div className="mt-1 font-normal text-gray-700">
                {rooms.find((room) => room.id === student.rooms[0])?.name ?? '-'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      label: t('familyDirectory.columns.familyContacts'),
      fieldName: 'FamilyandContacts',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!student.family || !Object.values(student.family)[memberIndex || 0]) return null;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        return (
          <div className="space-y-1">
            <Label>{member.displayName ? member.displayName : '-'}</Label>
            <div className="font-normal text-gray-700">{getFamilyRelationshipLabel(member, t)}</div>
          </div>
        );
      },
    },
    {
      label: t('familyDirectory.columns.contactInfo'),
      fieldName: 'ContactsInfo',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!student.family || !Object.values(student.family)[memberIndex || 0]) return null;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        return (
          <div className="space-y-1">
            <Label>{member.phone ? phoneNumberParse(member.phone) : '-'}</Label>
            <Label>{member.email ? member.email : '-'}</Label>
          </div>
        );
      },
    },
    {
      label: createLabelWithTooltip('common.pickup', 'familyDirectory.tooltips.pickup'),
      fieldName: 'Pickup',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!student.family || !Object.values(student.family)[memberIndex || 0]) return null;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        return <Label>{member.allowLoginWithPin ? <CheckmarkIcon className="size-5" /> : '-'}</Label>;
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.payment', 'familyDirectory.tooltips.payment'),
      fieldName: 'Payment',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!student.family || !Object.values(student.family)[memberIndex || 0]) return null;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        return (
          <div className="space-y-1">
            <Label>{student.responsibleForBilling?.id === member.id ? t('parentsListing.primary') : member.id}</Label>
            <div className="text-gray-600">
              {student.responsibleForBilling?.email === member.email && studentBillingDetails.includes(student.id)
                ? t('parentsListing.autoPay')
                : ''}
            </div>
          </div>
        );
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.app', 'familyDirectory.tooltips.app'),
      fieldName: 'App',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!allUsers || !student.family || !Object.values(student.family)[memberIndex || 0]) return <div>-</div>;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        const foundUser = allUsers.find((user) => user.relationship === 'parent' && user.email === member.email);

        if (foundUser) {
          return <div>{foundUser.stats?.appLastOpened ? <AppIcon className="size-5" /> : '-'}</div>;
        }
        return <div>-</div>;
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.invite', 'familyDirectory.tooltips.invite', 'max-w-32'),
      fieldName: 'Invite',
      className: 'py-2',
      renderCell: (student: any, memberIndex?: number) => {
        if (!student.family || !Object.values(student.family)[memberIndex || 0]) return null;
        const member = Object.values(student.family)[memberIndex || 0] as any;
        return InviteStatus(member, t);
      },
    },
  ];

  const desktopColumns = [
    {
      label: t('familyDirectory.columns.students'),
      fieldName: 'studentName',
      className: 'py-2',
      hideCell: (familyRow: any) => {
        return !familyRow.shouldShowStudentCell;
      },
      rowSpan: (familyRow: any) => {
        const student = studentsList.find((student: any) => student.id === familyRow.studentId);
        if (!student) return 1;

        // If this row shouldn't show the student cell, return 1 (no spanning)
        if (!familyRow.shouldShowStudentCell) return 1;

        // Calculate how many rows this student has on the current page
        const currentRowGlobalIndex = familySortedByStudent.findIndex((row) => row === familyRow);
        const currentPage = Math.floor(currentRowGlobalIndex / NUM_CONTACTS_PER_PAGE);
        const pageStartIndex = currentPage * NUM_CONTACTS_PER_PAGE;
        const pageEndIndex = Math.min(pageStartIndex + NUM_CONTACTS_PER_PAGE, familySortedByStudent.length);

        // Count rows for this student on the current page
        const rowsOnCurrentPage = familySortedByStudent
          .slice(pageStartIndex, pageEndIndex)
          .filter((row) => row.studentId === familyRow.studentId).length;

        return rowsOnCurrentPage;
      },
      renderCell: (familyRow: any) => {
        const student = studentsList.find((student: any) => student.id === familyRow.studentId);
        if (!student) return null;

        return (
          <div className="flex items-center">
            <div className="hidden h-11 w-11 flex-shrink-0 md:block">
              <div className="flex h-11 w-11 justify-center rounded-full text-center">
                {student.picture ? (
                  <Avatar name={student.picture} size={WidgetSizeEnum.MEDIUM} photoURL={student.picture} />
                ) : (
                  <Avatar name={student.displayName} size={WidgetSizeEnum.MEDIUM} />
                )}
              </div>
            </div>
            <div className="ml-4 cursor-default">
              <Label extraClasses="font-medium">{student.displayName}</Label>
              <div className="mt-1 font-normal text-gray-700">
                {rooms.find((room) => room.id === student.rooms[0])?.name ?? '-'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      label: t('familyDirectory.columns.familyContacts'),
      fieldName: 'FamilyandContacts',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        return (
          <div className="space-y-1">
            <Label>{familyRow.displayName ? familyRow.displayName : '-'}</Label>
            <div className="font-normal text-gray-700">{getFamilyRelationshipLabel(familyRow, t)}</div>
          </div>
        );
      },
    },
    {
      label: t('familyDirectory.columns.contactInfo'),
      fieldName: 'ContactsInfo',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        return (
          <div className="space-y-1">
            <Label>{familyRow.phone ? phoneNumberParse(familyRow.phone) : '-'}</Label>
            <Label>{familyRow.email ? familyRow.email : '-'}</Label>
          </div>
        );
      },
    },
    {
      label: createLabelWithTooltip('common.pickup', 'familyDirectory.tooltips.pickup'),
      fieldName: 'Pickup',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        return <Label>{familyRow.allowLoginWithPin ? <CheckmarkIcon className="size-5" /> : '-'}</Label>;
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.payment', 'familyDirectory.tooltips.payment'),
      fieldName: 'Payment',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        return (
          <div className="space-y-1">
            <Label>
              {familyRow.responsibleForBilling?.email === familyRow.email ? t('parentsListing.primary') : '-'}
            </Label>
            <div className="text-gray-600">
              {familyRow.responsibleForBilling?.email === familyRow.email &&
              studentBillingDetails.includes(familyRow.studentId)
                ? t('parentsListing.autoPay')
                : ''}
            </div>
          </div>
        );
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.app', 'familyDirectory.tooltips.app'),
      fieldName: 'App',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        if (!allUsers) return false;
        const foundUser = allUsers.find((user) => user.relationship === 'parent' && user.email === familyRow.email);

        if (foundUser) {
          return <div>{foundUser.stats?.appLastOpened ? <AppIcon className="size-5" /> : '-'}</div>;
        }
        return <div>-</div>;
      },
    },
    {
      label: createLabelWithTooltip('familyDirectory.columns.invite', 'familyDirectory.tooltips.invite', 'max-w-32'),
      fieldName: 'Invite',
      className: 'py-2',
      renderCell: (familyRow: any) => {
        return InviteStatus(familyRow, t);
      },
    },
  ];

  const rowClasses = 'even:bg-gray-100 even:border-b border-gray-300 min-h-24';
  const studentsListReport = useMemo<any>(() => {
    return studentsList
      .flatMap((student: any) => {
        if (student.family && Object.values(student.family).length > 0) {
          return Object.values(student.family).map((family: any) => {
            return {
              studentName: student?.displayName,
              rooms: rooms.find((room: any) => room.id === student.rooms[0])?.name,
              contacts: `${family?.displayName} (${getFamilyRelationshipLabel(family, t)})`,
              email: family?.email ? family.email : '',
              phone: family?.phone ? phoneNumberParse(family.phone) : '',
              pickup: family?.allowLoginWithPin ? (family.allowLoginWithPin ? t('common.yes') : t('common.no')) : '',
              payment: family?.email === student.responsibleForBilling?.email ? t('common.primaryPayer') : '-',
              autopay:
                family?.email === student.responsibleForBilling?.email && studentBillingDetails?.includes(student.id)
                  ? t('parentsListing.autoPay')
                  : '',
              app: family.stats?.appLastOpened ? t('common.yes') : t('common.no'),
              invite: getFamilyInviteStatusLabel(family.status, t),
              locationName: locationName,
            };
          });
        }
      })
      .filter((s: any) => !!s);
  }, [locationName, rooms, studentBillingDetails, studentsList, t]);

  const onExportPDF = useCallback(() => {
    setIsPDFModalOpen(true);
  }, []);

  const pdfGenerator = useCallback((report: any) => generateParentsListingPDF(report, t), [t]);

  const onExportCSV = useCallback(() => {
    generateParentsListingCSV(studentsList, rooms, studentBillingDetails, locationName, t);
  }, [rooms, studentsList, studentBillingDetails, locationName, t]);

  const familySortedByStudent = useMemo(() => {
    const allRows = studentsList.flatMap((student: any) => {
      return student.family && Object.values(student.family).length > 0
        ? Object.entries(student.family)?.map(([, member]: [string, any], index: number) => {
            const isFirst = index === 0;
            return {
              ...member,
              studentId: student.id,
              responsibleForBilling: student.responsibleForBilling,
              studentName: student.displayName,
              isFirstRowOfTheStudent: isFirst,
            };
          })
        : [];
    });

    // Add pagination-aware logic for showing student cells
    // We need to ensure that if a family spans across pages, the first row on each page shows the student info
    return allRows.map((row, globalIndex) => {
      const pageStartIndex = Math.floor(globalIndex / NUM_CONTACTS_PER_PAGE) * NUM_CONTACTS_PER_PAGE;
      const isFirstRowOnPage = globalIndex === pageStartIndex;

      // Check if this row is continuing the same student from previous page
      const previousRow = globalIndex > 0 ? allRows[globalIndex - 1] : null;
      const isContinuationOfPreviousStudent = previousRow && previousRow.studentId === row.studentId;

      // Show student cell if it's either:
      // 1. The first row of the student's family group, OR
      // 2. The first row on the current page AND it's continuing the same student
      const shouldShowStudentCell = row.isFirstRowOfTheStudent || (isFirstRowOnPage && isContinuationOfPreviousStudent);

      return {
        ...row,
        shouldShowStudentCell,
      };
    });
  }, [studentsList]);

  // Create mobile-friendly data structure that groups family members under students
  const mobileDataStructure = useMemo(() => {
    return studentsList.map((student: any) => {
      return {
        id: student.id,
        studentName: student.displayName,
        studentPicture: student.picture,
        rooms: student.rooms,
        family: student.family,
        responsibleForBilling: student.responsibleForBilling,
      };
    });
  }, [studentsList]);

  return (
    <>
      <div className="mt-2 px-4 sm:px-6 lg:px-8">
        {studentsList.length === 0 ? (
          <tr className="block text-center">
            <td colSpan={7} className="m-auto block text-center">
              <NoResults
                title="parentsListing.noParentsFound"
                subTitle="parentsListing.noParentsFoundSubTitle"
                data-testid="no-contacts-students"
              />
            </td>
          </tr>
        ) : (
          <>
            <div className="mt-4">
              <ReportToolbar onExportPDF={onExportPDF} onExportCSV={onExportCSV} />
            </div>
            {/* Desktop Table View */}
            <div className="hidden sm:block">
              <DataTable
                columns={desktopColumns}
                rowClasses={rowClasses}
                headerClasses="text-center"
                data={familySortedByStudent}
                displayMultipleInfoKey={undefined}
                numItemsPerPage={NUM_CONTACTS_PER_PAGE}
              />
            </div>

            {/* Mobile Card View */}
            <div className="block sm:hidden">
              <DataTable
                columns={mobileColumns}
                rowClasses={rowClasses}
                headerClasses="text-center"
                data={mobileDataStructure}
                displayMultipleInfoKey="family"
                numItemsPerPage={NUM_CONTACTS_PER_PAGE}
              />
            </div>
            <PDFModal
              isOpen={isPDFModalOpen}
              report={studentsListReport}
              pdfGenerator={pdfGenerator as any}
              onClose={() => setIsPDFModalOpen(false)}
            />
          </>
        )}
      </div>
    </>
  );
}

export default ParentsListingTable;
